import qs from "qs";

// Accumulated route state across all InstantSearch instances on the page.
// Each instance's write() merges its own index slice in; the full state is
// what gets serialised to the URL so all tabs' filters are always present.
let mergedState = null;
let writeTimer;

// qs turns an array longer than its default arrayLimit (20) into an index-keyed object, which
// crashes InstantSearch's refinementList — and a single region can refine 40+ countries.
const PARSE_OPTIONS = { arrayLimit: 1000 };

function read() {
  const raw = window.location.search.slice(1);
  return raw ? qs.parse(raw, PARSE_OPTIONS) : {};
}

// Seed lazily on first use, not at module load: tab components rewrite legacy deep-link
// params (e.g. ?hashtag=...) into InstantSearch routing params during their first render,
// and an eager seed would capture the consumed legacy key and re-append it to the URL on
// every subsequent write.
function ensureSeeded() {
  if (mergedState === null) mergedState = read();
}

function buildURL(state) {
  const queryString = qs.stringify(state, { encodeValuesOnly: true });
  // pathname is read at write time, not cached — as tabs change it now points at the current
  // tab's own path (e.g. /companies), and that's exactly where the filter state should land.
  const { pathname } = window.location;
  return queryString ? `${pathname}?${queryString}` : pathname;
}

// qs.stringify leaves refinement brackets literal ("experts[query]=..."), but the browser
// percent-encodes them in location.search ("experts%5Bquery%5D=..."). Compare decoded so a
// no-op write is recognised as such instead of pushing a duplicate history entry.
function sameUrl(a, b) {
  try {
    return decodeURIComponent(a) === decodeURIComponent(b);
  } catch {
    return a === b;
  }
}

// Turbolinks (v1) only restores a page on Back when the entry's state carries its
// { turbolinks: { restorationIdentifier } }; an entry pushed with a bare {} is ignored, so Back
// changes the URL without rendering. Reuse the current state to keep it. Null/no-op without Turbolinks.
function currentHistoryState() {
  return window.history.state;
}

// Designed for exactly one SourceDirectory mount per page — all four InstantSearch
// instances share this singleton so their URL states coexist rather than clobber.
const popstateHandles = [];

const sharedAlgoliaRouter = {
  read,

  write(routeState) {
    ensureSeeded();
    Object.keys(routeState).forEach((indexId) => {
      const s = routeState[indexId];
      if (!s || Object.keys(s).length === 0) {
        delete mergedState[indexId];
      } else {
        mergedState = { ...mergedState, [indexId]: s };
      }
    });

    clearTimeout(writeTimer);
    writeTimer = setTimeout(() => {
      const url = buildURL(mergedState);
      // Skip no-op writes: pushState adds a history entry even for an identical URL, and an
      // InstantSearch instance's first mount (e.g. visiting a tab) writes its initial state,
      // which serializes to the URL the tab navigation just pushed. Without this guard every
      // first visit to a tab costs two Back presses.
      if (sameUrl(url, `${window.location.pathname}${window.location.search}`)) return;
      window.history.pushState(currentHistoryState(), "", url);
    }, 400);
  },

  createURL(routeState) {
    ensureSeeded();
    const merged = { ...mergedState };
    Object.keys(routeState).forEach((indexId) => {
      const s = routeState[indexId];
      if (!s || Object.keys(s).length === 0) {
        delete merged[indexId];
      } else {
        merged[indexId] = s;
      }
    });
    return buildURL(merged);
  },

  onUpdate(cb) {
    const handle = () => {
      clearTimeout(writeTimer);
      mergedState = read();
      cb(mergedState);
    };
    popstateHandles.push(handle);
    window.addEventListener("popstate", handle);
    return () => {
      const idx = popstateHandles.indexOf(handle);
      if (idx !== -1) popstateHandles.splice(idx, 1);
      window.removeEventListener("popstate", handle);
    };
  },

  dispose() {
    clearTimeout(writeTimer);
    popstateHandles.forEach((h) => window.removeEventListener("popstate", h));
    popstateHandles.length = 0;
  },

  // Set a non-Algolia param (e.g. "saved") so it survives future Algolia URL writes.
  setParam(key, value) {
    ensureSeeded();
    if (value === null || value === undefined) {
      delete mergedState[key];
    } else {
      mergedState = { ...mergedState, [key]: value };
    }
    clearTimeout(writeTimer);
    writeTimer = setTimeout(() => {
      window.history.replaceState(currentHistoryState(), "", buildURL(mergedState));
    }, 400);
  },

  // Removes keys from the cached state without scheduling a write. Needed when a param is stripped
  // from the URL by something other than an InstantSearch write (e.g. SourceDirectory's tab-switch
  // strip of location_name/location_radius): the next write() re-serializes this whole cache and
  // would resurrect the param. The caller updates the visible URL itself.
  deleteParams(keys) {
    ensureSeeded();
    keys.forEach((key) => {
      delete mergedState[key];
    });
  },
};

export default sharedAlgoliaRouter;
